import React, { useState } from 'react';
import WidgetRenderer from './WidgetRenderer';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * ScreenRenderer — reads a screen layout definition and renders it as a form.
 *
 * Props:
 *   layout       — { sections: [...] } from screen_definitions.layout
 *   fields       — array of field definitions
 *   fieldData    — { field_id: value } current values
 *   onChange     — (fieldId, value) => void
 *   errors       — { field_id: errorMessage }
 *   readOnly     — boolean
 */
export default function ScreenRenderer({ layout, fields, fieldData, onChange, errors, readOnly, recordId, containerClassName, fieldLocks, incomingChanges, onFieldFocus, onFieldBlur, onFieldChange, clearIncomingChange, isLockedByOther }) {
  if (!layout || !layout.sections || layout.sections.length === 0) {
    return <FallbackRenderer fields={fields} fieldData={fieldData} onChange={onChange} errors={errors} readOnly={readOnly} recordId={recordId} />;
  }

  return (
    <div className="space-y-6">
      {layout.sections.map((section) => (
        <ScreenSection
          key={section.id}
          section={section}
          fields={fields}
          fieldData={fieldData}
          onChange={onChange}
          errors={errors}
          readOnly={readOnly}
          recordId={recordId}
          fieldLocks={fieldLocks}
          incomingChanges={incomingChanges}
          onFieldFocus={onFieldFocus}
          onFieldBlur={onFieldBlur}
          onFieldChange={onFieldChange}
          clearIncomingChange={clearIncomingChange}
          isLockedByOther={isLockedByOther}
        />
      ))}
    </div>
  );
}

/**
 * A single collapsible section with a grid of widgets
 */
function ScreenSection({ section, fields, fieldData, onChange, errors, readOnly, recordId, fieldLocks, incomingChanges, onFieldFocus, onFieldBlur, onFieldChange, clearIncomingChange, isLockedByOther }) {
  const [collapsed, setCollapsed] = useState(section.collapsed || false);

  // Evaluate section visibility
  if (section.visibility_rules && !evaluateVisibility(section.visibility_rules, fieldData)) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Section header */}
      {section.title && (
        <div
          className={`flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-200 ${
            section.collapsible ? 'cursor-pointer hover:bg-gray-100' : ''
          }`}
          onClick={() => section.collapsible && setCollapsed(!collapsed)}
        >
          <h3 className="text-sm font-semibold text-gray-700">{section.title}</h3>
          {section.collapsible && (
            collapsed
              ? <ChevronRight className="h-4 w-4 text-gray-400" />
              : <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      )}

      {/* Section content — CSS grid */}
      {!collapsed && (
        <div
          className="p-6 gap-4"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${section.columns || 12}, 1fr)`,
          }}
        >
          {(section.widgets || []).map((widget) => {
            // Evaluate widget visibility
            if (widget.visibility_rules && !evaluateVisibility(widget.visibility_rules, fieldData)) {
              return null;
            }

            return (
              <div
                key={widget.id}
                style={{
                  gridColumn: `${widget.col_start || 'auto'} / span ${widget.col_span || 1}`,
                  gridRow: widget.row_start ? `${widget.row_start} / span ${widget.row_span || 1}` : undefined,
                }}
              >
                <WidgetRenderer
                  widget={widget}
                  fields={fields}
                  fieldData={fieldData}
                  onChange={onChange}
                  errors={errors}
                  readOnly={readOnly}
                  recordId={recordId}
                  fieldLocks={fieldLocks}
                  incomingChanges={incomingChanges}
                  onFieldFocus={onFieldFocus}
                  onFieldBlur={onFieldBlur}
                  onFieldChange={onFieldChange}
                  clearIncomingChange={clearIncomingChange}
                  isLockedByOther={isLockedByOther}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Evaluates visibility rules against current field data.
 * Rules: { operator: 'AND'|'OR', conditions: [{ field_id, op, value }] }
 *
 * The optional `ctx` parameter carries the current user's team context so
 * the team-aware operators (user_in_teams / user_not_in_teams) can resolve.
 * The classic UI does not yet pass this context — Part 2B will wire it in
 * after the team filter helper is built. For now, callers without ctx still
 * work; team operators fall through to their safe defaults.
 *   ctx = { userTeamIds: [uuid...], userTeamNames: [string...], isAdmin: bool }
 */
function evaluateVisibility(rules, fieldData, ctx = {}) {
  if (!rules || !rules.conditions || rules.conditions.length === 0) return true;

  // Admin bypass for team-only rules — mirrors api/utils/fieldVisibility.js
  if (ctx.isAdmin === true) {
    const onlyTeamOps = rules.conditions.every(
      (c) => c.op === 'user_in_teams' || c.op === 'user_not_in_teams'
    );
    if (onlyTeamOps) return true;
  }

  const results = rules.conditions.map((cond) => {
    const fieldVal = fieldData[cond.field_id];
    switch (cond.op) {
      case 'equals': return fieldVal === cond.value;
      case 'not_equals': return fieldVal !== cond.value;
      case 'contains': return String(fieldVal || '').includes(cond.value);
      case 'not_empty': return fieldVal !== null && fieldVal !== undefined && fieldVal !== '';
      case 'empty': return !fieldVal && fieldVal !== 0 && fieldVal !== false;
      case 'greater_than': return Number(fieldVal) > Number(cond.value);
      case 'less_than': return Number(fieldVal) < Number(cond.value);
      case 'in': return Array.isArray(cond.value) && cond.value.includes(fieldVal);
      case 'array_includes': return Array.isArray(fieldVal) && fieldVal.includes(cond.value);
      case 'array_excludes': return !Array.isArray(fieldVal) || !fieldVal.includes(cond.value);

      // Team-aware operators (Teams Part 2A). cond.value is an array of team
      // names (strings) OR team IDs (uuids). Names are preferred — they're
      // portable across dev/qa/prod where UUIDs differ.
      case 'user_in_teams': {
        if (!Array.isArray(cond.value)) return false;
        const userTeamNames = ctx.userTeamNames || [];
        const userTeamIds = ctx.userTeamIds || [];
        return cond.value.some((t) => userTeamNames.includes(t) || userTeamIds.includes(t));
      }
      case 'user_not_in_teams': {
        if (!Array.isArray(cond.value)) return true;
        const userTeamNames = ctx.userTeamNames || [];
        const userTeamIds = ctx.userTeamIds || [];
        return !cond.value.some((t) => userTeamNames.includes(t) || userTeamIds.includes(t));
      }

      default: return true;
    }
  });

  if (rules.operator === 'OR') return results.some(Boolean);
  return results.every(Boolean); // AND is default
}

/**
 * Fallback: if no screen layout, render all fields in a simple list
 * (backwards compatible with Phase 1 behavior)
 */
function FallbackRenderer({ fields, fieldData, onChange, errors, readOnly, recordId }) {
  // Import FieldInput dynamically to avoid circular deps
  const { FieldInput } = require('../../pages/RecordCreate');

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
      {fields.map((field) => (
        <FieldInput
          key={field.id}
          field={field}
          value={fieldData[field.id]}
          onChange={readOnly ? () => {} : (val) => onChange(field.id, val)}
          error={errors[field.id]}
          recordId={recordId}
        />
      ))}
    </div>
  );
}

export { evaluateVisibility };
